import { useRef, useCallback, useState } from "react";
import { Camera, X, Plus, Replace, Loader2, AlertCircle, Crop } from "lucide-react";
import { OnboardingPhoto } from "@/hooks/useOnboarding";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "@/hooks/use-toast";
import PhotoCropModal from "./PhotoCropModal";

interface OnboardingPhotoUploadProps {
  photos: OnboardingPhoto[];
  onAddPhoto: (file: File) => void;
  onRemovePhoto: (index: number) => void;
  onReplacePhoto: (index: number, file: File) => void;
  maxPhotos?: number;
}

const OnboardingPhotoUpload = ({
  photos,
  onAddPhoto,
  onRemovePhoto,
  onReplacePhoto,
  maxPhotos = 5,
}: OnboardingPhotoUploadProps) => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const replaceInputRefs = useRef<(HTMLInputElement | null)[]>([]);
  const [validatingIndex, setValidatingIndex] = useState<number | null>(null);
  const [validatingNew, setValidatingNew] = useState(false);
  const [faceErrors, setFaceErrors] = useState<Record<number, string>>({});
  
  // Crop modal state
  const [showCropModal, setShowCropModal] = useState(false);
  const [cropImageSrc, setCropImageSrc] = useState<string | null>(null);
  const [cropMode, setCropMode] = useState<"add" | "replace">("add");
  const [cropReplaceIndex, setCropReplaceIndex] = useState<number>(0);

  const convertToBase64 = useCallback((file: File): Promise<string> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => {
        const result = reader.result as string;
        const base64 = result.split(",")[1];
        resolve(base64);
      };
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });
  }, []);

  const detectFace = useCallback(async (file: File): Promise<{ hasFace: boolean; reason: string }> => {
    try {
      const base64 = await convertToBase64(file);
      
      const { data, error } = await supabase.functions.invoke("detect-face", {
        body: { imageBase64: base64 },
      });

      if (error) {
        console.error("Face detection error:", error);
        return { hasFace: true, reason: "Service unavailable" };
      }

      return data;
    } catch (err) {
      console.error("Face detection error:", err);
      return { hasFace: true, reason: "Service unavailable" };
    }
  }, [convertToBase64]);

  const openCropModal = useCallback((file: File, mode: "add" | "replace", replaceIndex = 0) => {
    const url = URL.createObjectURL(file);
    setCropImageSrc(url);
    setCropMode(mode);
    setCropReplaceIndex(replaceIndex);
    setShowCropModal(true);
  }, []);

  const handleCropComplete = useCallback(async (croppedBlob: Blob) => {
    // Convert blob to file
    const file = new File([croppedBlob], `photo-${Date.now()}.jpg`, { type: "image/jpeg" });
    
    // Close modal first
    if (cropImageSrc) {
      URL.revokeObjectURL(cropImageSrc);
    }
    setShowCropModal(false);
    setCropImageSrc(null);

    // Validate face
    if (cropMode === "add") {
      setValidatingNew(true);
      setFaceErrors({});

      const result = await detectFace(file);
      
      if (result.hasFace) {
        onAddPhoto(file);
      } else {
        toast({
          title: "No face detected",
          description: result.reason || "Please upload a photo with a visible face",
          variant: "destructive",
        });
      }
      
      setValidatingNew(false);
    } else {
      setValidatingIndex(cropReplaceIndex);
      
      const newErrors = { ...faceErrors };
      delete newErrors[cropReplaceIndex];
      setFaceErrors(newErrors);

      const result = await detectFace(file);
      
      if (result.hasFace) {
        onReplacePhoto(cropReplaceIndex, file);
      } else {
        setFaceErrors((prev) => ({ ...prev, [cropReplaceIndex]: result.reason || "No face detected" }));
        toast({
          title: "No face detected",
          description: result.reason || "Please upload a photo with a visible face",
          variant: "destructive",
        });
      }
      
      setValidatingIndex(null);
    }
  }, [cropMode, cropReplaceIndex, cropImageSrc, detectFace, faceErrors, onAddPhoto, onReplacePhoto]);

  const handleCloseCropModal = useCallback(() => {
    if (cropImageSrc) {
      URL.revokeObjectURL(cropImageSrc);
    }
    setShowCropModal(false);
    setCropImageSrc(null);
  }, [cropImageSrc]);

  const handleFileSelect = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      if (file) {
        openCropModal(file, "add");
      }
      e.target.value = "";
    },
    [openCropModal]
  );

  const handleReplaceSelect = useCallback(
    (index: number, e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      if (file) {
        openCropModal(file, "replace", index);
      }
      e.target.value = "";
    },
    [openCropModal]
  );

  const remainingSlots = maxPhotos - photos.length;
  const showAddButton = remainingSlots > 0;

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold">Photos</h3>
        <span className="text-sm text-muted-foreground">
          {photos.length}/{maxPhotos} photos
        </span>
      </div>

      <p className="text-sm text-muted-foreground">
        Add at least 1 photo with a visible face. Photos will be cropped to 3:4 ratio.
      </p>

      <div className="grid grid-cols-3 gap-3">
        {/* Existing photos */}
        {photos.map((photo, index) => (
          <div
            key={index}
            className={`relative aspect-[3/4] rounded-2xl overflow-hidden bg-muted group ${
              faceErrors[index] ? "ring-2 ring-destructive" : ""
            }`}
          >
            <img
              src={photo.preview}
              alt={`Photo ${index + 1}`}
              className="w-full h-full object-cover"
            />

            {/* Primary badge */}
            {index === 0 && (
              <div className="absolute top-2 left-2 px-2 py-1 rounded-full bg-pop text-pop-foreground text-xs font-medium">
                Main
              </div>
            )}

            {/* Validation overlay */}
            {validatingIndex === index && (
              <div className="absolute inset-0 bg-black/60 flex items-center justify-center">
                <div className="text-center text-white">
                  <Loader2 className="w-8 h-8 animate-spin mx-auto mb-2" />
                  <p className="text-xs">Checking face...</p>
                </div>
              </div>
            )}

            {/* Error indicator */}
            {faceErrors[index] && (
              <div className="absolute top-2 right-2 p-1 rounded-full bg-destructive">
                <AlertCircle className="w-4 h-4 text-white" />
              </div>
            )}

            {/* Action overlay */}
            {validatingIndex !== index && (
              <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2">
                {/* Replace button */}
                <button
                  onClick={() => replaceInputRefs.current[index]?.click()}
                  className="p-2 rounded-full bg-white/20 backdrop-blur-sm hover:bg-white/30 transition-colors"
                  title="Replace with new photo"
                >
                  <Replace className="w-4 h-4 text-white" />
                </button>
                <input
                  ref={(el) => (replaceInputRefs.current[index] = el)}
                  type="file"
                  accept="image/*"
                  onChange={(e) => handleReplaceSelect(index, e)}
                  className="hidden"
                />

                {/* Remove button */}
                <button
                  onClick={() => {
                    onRemovePhoto(index);
                    const newErrors = { ...faceErrors };
                    delete newErrors[index];
                    setFaceErrors(newErrors);
                  }}
                  className="p-2 rounded-full bg-destructive/80 backdrop-blur-sm hover:bg-destructive transition-colors"
                >
                  <X className="w-4 h-4 text-white" />
                </button>
              </div>
            )}
          </div>
        ))}

        {/* Add photo button */}
        {showAddButton && (
          <button
            onClick={() => fileInputRef.current?.click()}
            disabled={validatingNew}
            className="aspect-[3/4] rounded-2xl border-2 border-dashed border-border hover:border-pop transition-colors flex flex-col items-center justify-center gap-2 text-muted-foreground hover:text-pop disabled:opacity-50"
          >
            {validatingNew ? (
              <>
                <Loader2 className="w-8 h-8 animate-spin" />
                <span className="text-xs">Checking...</span>
              </>
            ) : photos.length === 0 ? (
              <>
                <Camera className="w-8 h-8" />
                <span className="text-sm font-medium">Add Photo</span>
                <Crop className="w-4 h-4 opacity-50" />
              </>
            ) : (
              <>
                <Plus className="w-6 h-6" />
                <span className="text-xs">Add More</span>
              </>
            )}
          </button>
        )}

        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          onChange={handleFileSelect}
          className="hidden"
        />
      </div>

      {photos.length === 0 && (
        <p className="text-sm text-destructive">At least one photo is required</p>
      )}

      {/* Crop Modal */}
      {cropImageSrc && (
        <PhotoCropModal
          isOpen={showCropModal}
          imageSrc={cropImageSrc}
          onClose={handleCloseCropModal}
          onCropComplete={handleCropComplete}
          aspectRatio={3 / 4}
        />
      )}
    </div>
  );
};

export default OnboardingPhotoUpload;
